import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useConnectionState, useRoomContext } from '@livekit/components-react'
import {
  ConnectionState,
  createLocalTracks,
  ParticipantEvent,
  Room,
  RoomEvent,
  Track,
  TrackEvent,
  VideoPresets,
  type LocalAudioTrack,
  type LocalParticipant,
  type LocalTrack,
  type LocalVideoTrack,
  type TrackPublishOptions,
} from 'livekit-client'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import { watchApi } from './api'
import { deviceChoices } from './devices'
import { retryDelayMs } from './reconnect'
import { parseRoomMetadata } from './rules'
import type { StageFace, WatchApiFailure } from './types'

/** How long going live waits for LiveKit to raise this connection after the API took the seat. */
export const PERMISSION_TIMEOUT_MS = 10_000

/**
 * The room metadata's slack around a seat. After the API's answer, a metadata update without this connection only
 * counts once the metadata has listed it or this long has passed (the API publishes the metadata as it answers, and it
 * can arrive later). While LiveKit still lets this connection publish, an update without it is checked against the
 * latest metadata this long after, before it ends the live: one snapshot can be stale, and a real move of the seat
 * lowers this connection at once. A lowering this soon after a refused End is End's `DELETE` landing (its answer lost).
 */
export const SEAT_SETTLE_MS = 5_000

/** How long the check waits for the new connection after a 409 `connection_gone` before it stops saying Reconnecting. */
export const RECONNECT_TIMEOUT_MS = 15_000

/**
 * - `idle`: watching.
 * - `checking`: the camera check; local camera and microphone tracks are open for the preview, nothing is published.
 * - `joining`: the API is taking the seat and LiveKit is raising this connection; nothing says live yet.
 * - `live`: LiveKit has published the camera and microphone.
 */
export type StagePresenceState = 'idle' | 'checking' | 'joining' | 'live'

export type StagePresenceErrorCode =
  | 'media_insecure'
  | 'media_unsupported'
  | 'media_denied'
  | 'camera_missing'
  | 'mic_missing'
  | 'media_missing'
  | 'media_busy'
  | 'media_failed'
  | 'camera_stopped'
  | 'mic_stopped'
  | 'switch_failed'
  | 'reconnecting'
  | 'connection_gone'
  | 'stage_full'
  | 'username_required'
  | 'forbidden'
  | 'livekit_unavailable'
  | 'rate_limited'
  | 'network'
  | 'go_live_failed'
  | 'permission_timeout'
  | 'publish_failed'
  | 'seat_lost'
  | 'left_stage'
  | 'disconnected'
  | 'not_host'
  | 'live_ended'
  | 'spot_held'
  | 'already_on_stage'
  | 'not_connected'
  | 'cancelled'

export interface StagePresenceError {
  code: StagePresenceErrorCode
  message: string
}

/**
 * Who this tab goes on stage as: one of the stage's hosts, or a guest a host invited (from chat, or by the stage's
 * link). The server says which when it takes the seat.
 */
export type StageRole = 'host' | 'guest'

/** What a guest picked in the camera check (`CameraCheckSheet`'s choice), to join with. */
export interface StageJoinChoice {
  /** The camera to publish, or null to join with the microphone alone (there is none). */
  videoDeviceId: string | null
  audioDeviceId: string | null
  cameraOn: boolean
  micOn: boolean
}

/** A join that didn't put the guest on stage: why, in words ready to show (the camera check says them). */
export class StagePresenceFailure extends Error {
  readonly code: StagePresenceErrorCode

  constructor(error: StagePresenceError) {
    super(error.message)
    this.name = 'StagePresenceFailure'
    this.code = error.code
  }
}

/** A camera or microphone the browser lists. */
export interface StageDevice {
  deviceId: string
  label: string
}

const MESSAGES: Record<StagePresenceErrorCode, string> = {
  media_insecure: 'Your camera needs a secure connection. Open this page with https.',
  media_unsupported: 'This browser can’t use your camera. Try another browser.',
  media_denied: 'Allow camera and microphone for this site. Your device settings can block the browser too.',
  camera_missing: 'We can’t find a camera. Connect one, then try again.',
  mic_missing: 'We can’t find a microphone. Connect one, then try again.',
  media_missing: 'We can’t find a camera or microphone. Connect them, then try again.',
  media_busy: 'Another app is using your camera or microphone. Close it, then try again.',
  media_failed: 'We couldn’t start your camera or microphone. Try again.',
  camera_stopped: 'Your camera stopped. Turn it back on.',
  mic_stopped: 'Your microphone stopped. Turn it back on.',
  switch_failed: 'We couldn’t switch to that one. Try again.',
  reconnecting: 'Reconnecting…',
  connection_gone: 'We couldn’t reconnect. Try again in a moment.',
  stage_full: 'The stage is full.',
  username_required: 'Pick a username to go live.',
  forbidden: 'Only this stage’s hosts can go live.',
  livekit_unavailable: 'Going live isn’t working right now. Try again in a moment.',
  rate_limited: 'That’s a lot of tries. Wait a minute, then try again.',
  network: 'We couldn’t reach StreamBridge. Check your connection and try again.',
  go_live_failed: 'We couldn’t go live. Try again.',
  permission_timeout: 'Going live took too long. Try again.',
  publish_failed: 'We couldn’t send your camera and microphone. Try again.',
  seat_lost: 'You’re no longer live in this tab.',
  left_stage: 'You’re off the stage. Go live to come back.',
  disconnected: 'Your connection dropped, so you’re no longer live.',
  not_host: 'You’re no longer a host of this stage.',
  live_ended: 'Your live ended.',
  spot_held: 'We couldn’t free your spot. Close this tab to free it.',
  already_on_stage: 'You’re on another stage of this event. End that live first.',
  not_connected: 'We’re still connecting. Try again in a moment.',
  cancelled: 'You didn’t go on stage.',
}

/** A guest's words where a host's don't fit (a guest goes on stage by invitation, and leaves rather than ends). */
const GUEST_MESSAGES: Partial<Record<StagePresenceErrorCode, string>> = {
  forbidden: 'Your invitation is no longer open. Ask a host to invite you again.',
  stage_full: 'The stage is full now. Try again when a seat frees up.',
  already_on_stage: 'You’re on another stage of this event. Leave it first.',
  username_required: 'Pick a username to join the stage.',
  livekit_unavailable: 'Joining isn’t working right now. Try again in a moment.',
  go_live_failed: 'We couldn’t put you on stage. Try again.',
  // After the seat was taken: the seat goes back keeping the invitation (`reason=undo`) once the API honours it; until
  // then the invitation is used up, so Join may be refused.
  permission_timeout: 'Joining took too long. If Join stage doesn’t work, ask a host to invite you again.',
  publish_failed: 'We couldn’t send your camera and microphone. If Join stage doesn’t work, ask a host to invite you again.',
  seat_lost: 'You’re on the stage in another tab.',
  left_stage: 'You’re off the stage.',
  disconnected: 'Your connection dropped, so you’re off the stage.',
  live_ended: 'You left the stage.',
  spot_held: 'We couldn’t free your seat. Close this tab to free it.',
}

/** The error in the words for who went on stage. */
function wordsFor(error: StagePresenceError, role: StageRole): StagePresenceError {
  const words = role === 'guest' ? GUEST_MESSAGES[error.code] : undefined
  return words ? { code: error.code, message: words } : error
}

const presenceError = (code: StagePresenceErrorCode): StagePresenceError => ({ code, message: MESSAGES[code] })

/** A 429 says when to try again, from its Retry-After. */
function refusal(code: StagePresenceErrorCode, failure: WatchApiFailure): StagePresenceError {
  const wait = code === 'rate_limited' ? waitWords(failure.retryAfterMs) : null
  return wait ? { code, message: `That’s a lot of tries. Try again in ${wait}.` } : presenceError(code)
}

export interface StagePresenceOptions {
  /** The stage to go live on; null until the stage loads. */
  stageId: string | null
  /**
   * This tab's LiveKit identity (WatchRoom's `identity`); null before the first watch token. Going live waits until
   * the room is connected as this identity, and posts the identity the room is connected as.
   */
  identity: string | null
  /**
   * The stage's live members as the room metadata lists them (`useWatchScreen`'s `roomMembers`); null while the
   * metadata carries no stages. Each new array is a metadata update. While live, one that no longer lists this
   * connection takes it off the stage, and after a full reconnect the seat is asked for again only while the metadata
   * still lists this connection.
   */
  roomMembers?: readonly Pick<StageFace, 'identity' | 'userId'>[] | null
  /**
   * A 409 `connection_gone`: this connection is gone from the room, so fetch a new watch token (WatchRoom's `refresh`).
   * Going live tries once more by itself when the room has joined as the new identity.
   */
  onReconnectNeeded(): void
  /**
   * A 422 `username_required`: show the username step, then call `goLive()` again. A fallback: the page checks for
   * a username before it opens the check (HostDock's `onGoLive`), since a sheet over the check would close it.
   */
  onUsernameNeeded?(): void
}

export interface StagePresence {
  state: StagePresenceState
  /**
   * Who this tab goes (or last went) on stage as: the server's word once it took the seat, before that what was asked
   * for (`check` and `goLive` a host's live, `join` a guest's). Errors are in their words.
   */
  role: StageRole
  /** What went wrong last, in words for the host or guest; cleared by the next check, go live, join or leave. */
  error: StagePresenceError | null
  /**
   * The local camera for the preview, while the check or the live is open. Mirror it; it's muted video. Null for a
   * guest who joined with the microphone alone.
   */
  camera: LocalVideoTrack | null
  micOn: boolean
  cameraOn: boolean
  /** Cameras and microphones to pick from, once the check has them open. The browser's default entry is left out. */
  cameras: StageDevice[]
  microphones: StageDevice[]
  cameraId: string | null
  microphoneId: string | null
  /** The check's tracks are open, there is a stage, and the room is connected as `identity` (not one going away). */
  canGoLive: boolean
  /**
   * Live, and the room is reconnecting or asking for the seat again after a reconnect: the audience can't see or hear
   * the host meanwhile. The controls stay.
   */
  reconnecting: boolean
  /** End won over a refusal: off the stage, and the seat is still being given back (`spot_held` if that fails too). */
  freeing: boolean
  /** Opens the camera check: local camera and microphone, nothing published. Again after a refusal, it retries. */
  check(): Promise<void>
  /** Takes the seat, waits for LiveKit to raise this connection, then publishes the checked tracks. */
  goLive(): Promise<void>
  /**
   * A guest's join, after the page's own camera check (`CameraCheckSheet`) handed its devices back: opens the camera
   * and microphone picked there (on or off as picked; the microphone alone without a camera) and goes live as
   * `goLive` does. Resolves once live. Rejects with a `StagePresenceFailure` in a guest's words when it doesn't get
   * there, with the camera and microphone off again (a lapsed invitation, a full stage, a cancel); a 409
   * `connection_gone` waits for the new connection and tries once more first. A seat the API took for a join that then
   * fails or is cancelled goes back with `reason=undo` (the invitation kept); only `leave` uses it up.
   */
  join(choice: StageJoinChoice): Promise<void>
  /** Closes the check (camera light off). While joining it also gives back a seat the API took (a guest's with `undo`). */
  cancel(): void
  /** Ends the live: frees the seat, unpublishes and stops the tracks. Rejects, still live, when the API refuses while
   * LiveKit publishes both tracks; otherwise End wins (`freeing`). */
  leave(): Promise<void>
  toggleMic(): Promise<void>
  toggleCamera(): Promise<void>
  selectCamera(deviceId: string): Promise<void>
  selectMicrophone(deviceId: string): Promise<void>
}

/** One go live, from the API call until it ends. While live it holds the seat. */
interface Attempt {
  stageId: string
  /**
   * Who went on stage (the server's word once it answered). A guest's seat given back on a fault, a cancel or leaving
   * the screen keeps their invitation (`reason=undo`); only their own Leave stage uses it up.
   */
  role: StageRole
  /** The person's own Leave stage (End won): the seat goes back as a leave, a guest's invitation used up. */
  left: boolean
  /** The connection that went live, so freeing the seat names it. */
  identity: string
  /** LiveKit's session for that connection when last checked: a full reconnect rejoins as a new one, a resume doesn't. */
  sid: string | undefined
  /** The API answered: this connection holds the seat. */
  seat: boolean
  /**
   * When the API answered going live, for the metadata's slack. A restore needs none: it asks only while the metadata
   * lists this connection.
   */
  answeredAt: number
  /** The room metadata has listed this connection since that answer. */
  listed: boolean
  /** Its user id as the metadata listed it, to tell another tab of the same user taking the seat. */
  userId: string | null
  /**
   * When End was refused while staying live: a lowering within `SEAT_SETTLE_MS` is the live ending (End's answer may
   * have been lost). Cleared by a later metadata update that lists this connection: End didn't land.
   */
  endRefusedAt: number | null
  /** A restore's ask took the seat again while End was under way: End frees it once more. */
  askLanded: boolean
  /** Ended before (or after) it finished; `free` says whether its seat goes back once the API answers. */
  ended: boolean
  free: boolean
  abort: AbortController
}

interface CheckedTracks {
  /** Null for a guest joining with the microphone alone. */
  camera: LocalVideoTrack | null
  mic: LocalAudioTrack
}

type MediaKind = keyof CheckedTracks

/** LiveKit's permission type (from its protocol package, which this app doesn't import). */
type ParticipantPermission = NonNullable<LocalParticipant['permissions']>

const STOPPED: Record<MediaKind, StagePresenceErrorCode> = { camera: 'camera_stopped', mic: 'mic_stopped' }

type RoomMembers = StagePresenceOptions['roomMembers']
type RoomMember = NonNullable<RoomMembers>[number]
const listedMember = (members: RoomMembers, identity: string) => members?.find((member) => member.identity === identity)

/** The user id in a watch token's identity, `u_<user id>.<8 hex>` (the API's `ViewerIdentity.for_user`). */
const identityUserId = (identity: string) => /^u_(.+)\.[0-9a-f]{8}$/.exec(identity)?.[1] ?? null

/** The metadata seats this attempt's user on another connection: another tab took the seat. */
function seatedElsewhere(members: readonly RoomMember[], attempt: Attempt) {
  const userId = attempt.userId ?? identityUserId(attempt.identity)
  if (userId === null) return false
  return members.some(
    (member) => member.identity !== attempt.identity && (member.userId === userId || identityUserId(member.identity) === userId),
  )
}

/** End was refused a moment ago while staying live, and nothing since says its `DELETE` didn't land. */
const endJustRefused = (attempt: Attempt) => attempt.endRefusedAt !== null && Date.now() - attempt.endRefusedAt < SEAT_SETTLE_MS

/** Worth one more `DELETE` after its wait: no answer, the throttle, or the server failing. */
const retryable = (failure: WatchApiFailure) => failure.status === null || failure.status === 429 || failure.status >= 500

/** The camera's publish options. The provider's room options turn on dynacast and adaptive stream; simulcast lets
 * viewers take a small layer. Encodings are stated: LiveKit picks them from the capture size, and a camera published
 * while off has none, so it would fall back to 1080p's. */
const CAMERA_PUBLISH: TrackPublishOptions = {
  source: Track.Source.Camera,
  simulcast: true,
  videoEncoding: VideoPresets.h720.encoding,
  videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
}

const publications = (tracks: CheckedTracks): [LocalTrack, TrackPublishOptions][] => [
  ...(tracks.camera ? [[tracks.camera, CAMERA_PUBLISH] satisfies [LocalTrack, TrackPublishOptions]] : []),
  [tracks.mic, { source: Track.Source.Microphone }],
]

/** LiveKit publishes this track now (a full reconnect unpublishes, and republishes only with the permission). */
const isPublished = (participant: LocalParticipant, track: LocalTrack) => participant.getTrackPublication(track.source)?.track === track

/** LiveKit lets this connection publish and publishes both tracks (the microphone alone without a camera): nothing was
 * lost (a resume keeps all of it). */
const publishingBoth = (participant: LocalParticipant, tracks: CheckedTracks) =>
  Boolean(participant.permissions?.canPublish) && publications(tracks).every(([track]) => isPublished(participant, track))

/** A full reconnect: reconnecting as a new LiveKit session, whose join response (it sets the sid first) applied the
 * watch token's permission. A resume can say `Reconnecting` too (the engine went offline), but keeps the session. */
const rejoining = (room: Room, attempt: Attempt) =>
  room.state === ConnectionState.Reconnecting && room.localParticipant.sid !== attempt.sid

/** `POST /live` again with the same identity (`go_live!` keeps the seat and its place); the refusal, or null. */
const askAgain = (attempt: Attempt) => watchApi.goLive(attempt.stageId, attempt.identity).then(() => null, apiFailure)

/**
 * `DELETE /live?identity=`; the refusal, or null. A guest's seat goes back with `reason=undo` unless they left
 * themselves: a join that failed, a cancel or a restore that gave up never uses their invitation up.
 */
const leaveSeat = (attempt: Attempt) =>
  (attempt.role === 'guest' && !attempt.left
    ? watchApi.leave(attempt.stageId, attempt.identity, 'undo')
    : watchApi.leave(attempt.stageId, attempt.identity)
  ).then(() => null, apiFailure)

/** The API raised this participant on this stage, as a host or a guest (`StageLiveController#participant_metadata`). */
function raisedOnStage(metadata: string | undefined, stageId: string) {
  try {
    const parsed = JSON.parse(metadata ?? '') as { role?: unknown; stage_id?: unknown } | null
    return (parsed?.role === 'host' || parsed?.role === 'guest') && parsed.stage_id === stageId
  } catch {
    return false
  }
}

/**
 * Why asking for the seat again after a reconnect failed: the throttle, the role (a host no longer one; a guest taken
 * off the stage, their invitation gone), or the drop.
 */
function restoreRefusal(failure: WatchApiFailure, role: StageRole): StagePresenceError {
  if (failure.status === 429) return refusal('rate_limited', failure)
  if (failure.status === 403) return presenceError(role === 'guest' ? 'left_stage' : 'not_host')
  return presenceError('disconnected')
}

/** The role the API's go live answer names (`StageMemberPayload.role`), if it names one. */
function servedRole(answer: unknown): StageRole | null {
  const role = (answer as { data?: { role?: unknown } } | null | undefined)?.data?.role
  return role === 'host' || role === 'guest' ? role : null
}

const noop = () => {}

/**
 * The host's own presence on their stage from the watch screen: a local camera check, going live on this tab's
 * existing room connection (no reconnect: the API raises its permission and LiveKit applies it), and leaving.
 *
 * Honest state: `live` only once LiveKit has published, and it keeps itself honest, since LiveKit may send the API no
 * webhooks and the API frees an absent connection's seat only after about a minute:
 * - The room metadata (`roomMembers`) stops listing this connection: off the stage, without calling the API (while
 *   LiveKit still lets it publish, once the latest metadata a moment later agrees).
 * - LiveKit lowers this connection while connected or resuming (another tab went live, the host left elsewhere): off
 *   the stage, without calling the API (`DELETE /live` frees whichever connection holds the user's seat, and the
 *   metadata that says where it went can be slow).
 * - A full reconnect rejoins with the watch token's permission: the seat is asked for again on the same connection,
 *   only while the room metadata still lists this connection and no other tab of this user is raised on this stage
 *   (never taking it back from another tab, never retaking a freed one), and what LiveKit couldn't republish is
 *   published. Another reconnect meanwhile cuts the restore short and asks again with fresh metadata rather than
 *   dropping. A resume keeps everything: nothing to ask.
 * - End frees the seat. Refused while LiveKit publishes both tracks, it stays live (End says so). Refused otherwise
 *   (a restore under way), End wins: off the stage at once, and the seat is freed once more after the refusal's wait.
 *   After End is tapped nothing asks for the seat or publishes again.
 * - The connection closes or a new token replaces it: off the stage; the API frees the closed connection's seat.
 * Every async step is checked against cancellation, unmount and a new connection, so a late answer never publishes or
 * flips the state.
 *
 * Needs the room context (inside `WatchRoom`).
 */
export function useStagePresence({
  stageId,
  identity,
  roomMembers = null,
  onReconnectNeeded,
  onUsernameNeeded,
}: StagePresenceOptions): StagePresence {
  const room = useRoomContext()
  const connectionState = useConnectionState(room)
  const [state, setState] = useState<StagePresenceState>('idle')
  const [role, setRoleState] = useState<StageRole>('host')
  const [error, setError] = useState<StagePresenceError | null>(null)
  const [camera, setCamera] = useState<LocalVideoTrack | null>(null)
  /** The open tracks, whether or not there is a camera: what the tracks' own listeners follow. */
  const [media, setMedia] = useState<CheckedTracks | null>(null)
  const [micOn, setMicOn] = useState(true)
  const [cameraOn, setCameraOn] = useState(true)
  const [cameras, setCameras] = useState<StageDevice[]>([])
  const [microphones, setMicrophones] = useState<StageDevice[]>([])
  const [cameraId, setCameraId] = useState<string | null>(null)
  const [microphoneId, setMicrophoneId] = useState<string | null>(null)
  /** The connection a go live found gone: the next one goes live by itself once the room joins as it. */
  const [reconnectFrom, setReconnectFrom] = useState<string | null>(null)
  /** Restoring after a reconnect, for `reconnecting`. */
  const [restoring, setRestoring] = useState(false)
  const [freeing, setFreeing] = useState(false)

  const phaseRef = useRef<StagePresenceState>('idle')
  const roleRef = useRef<StageRole>('host')
  /** A guest's join waiting to settle (`join`): it resolves once live and rejects once it can't get there. */
  const joinRef = useRef<{ resolve: () => void; reject: (failure: StagePresenceFailure) => void; started: boolean } | null>(null)
  const tracksRef = useRef<CheckedTracks | null>(null)
  const publishedRef = useRef(new Set<LocalTrack>())
  /** A check whose tracks are still opening; cleared to drop them when they arrive. */
  const openingRef = useRef<object | null>(null)
  const attemptRef = useRef<Attempt | null>(null)
  /** This tab's own leave is under way, so the permission drop it causes isn't a lost seat. */
  const leavingRef = useRef(false)
  /** The running restore pass; a newer `Reconnected` or the attempt ending aborts it. */
  const restoreRef = useRef<AbortController | null>(null)
  /** Giving the seat back after End won (`freeSeat`); a new go live or leaving the screen aborts it. */
  const freeingRef = useRef<AbortController | null>(null)
  /** The attempt whose seat `freeSeat` couldn't give back: cancelling a camera check says so again (`spot_held`). */
  const unfreedRef = useRef<Attempt | null>(null)
  /** A metadata update without this connection while LiveKit still lets it publish: the check of the latest metadata. */
  const recheckRef = useRef<number | null>(null)
  const membersRef = useRef<RoomMembers>(roomMembers)
  const micOnRef = useRef(true)
  const cameraOnRef = useRef(true)
  const optionsRef = useRef({ stageId, identity, onReconnectNeeded, onUsernameNeeded })

  useEffect(() => {
    optionsRef.current = { stageId, identity, onReconnectNeeded, onUsernameNeeded }
  }, [stageId, identity, onReconnectNeeded, onUsernameNeeded])

  const setPhase = useCallback((next: StagePresenceState) => {
    phaseRef.current = next
    setState(next)
  }, [])

  const setRole = useCallback((next: StageRole) => {
    roleRef.current = next
    setRoleState(next)
  }, [])

  /** Takes this tab off the stage in LiveKit and turns the camera and microphone off. No state. */
  const releaseMedia = useCallback(() => {
    openingRef.current = null
    const participant = room.localParticipant
    publishedRef.current.forEach((track) => void participant.unpublishTrack(track, true).catch(noop))
    publishedRef.current.clear()
    const tracks = tracksRef.current
    tracksRef.current = null
    tracks?.camera?.stop()
    tracks?.mic.stop()
  }, [room])

  const setOn = useCallback((kind: MediaKind, on: boolean) => {
    if (kind === 'camera') {
      cameraOnRef.current = on
      setCameraOn(on)
    } else {
      micOnRef.current = on
      setMicOn(on)
    }
  }, [])

  const resetMedia = useCallback(() => {
    micOnRef.current = true
    cameraOnRef.current = true
    setCamera(null)
    setMedia(null)
    setMicOn(true)
    setCameraOn(true)
    setCameras([])
    setMicrophones([])
    setCameraId(null)
    setMicrophoneId(null)
  }, [])

  const cancelRecheck = useCallback(() => {
    if (recheckRef.current !== null) window.clearTimeout(recheckRef.current)
    recheckRef.current = null
  }, [])

  /** Ends the current attempt and its restore; with `free`, gives its seat back now or once the API answers. */
  const endAttempt = useCallback(
    (free: boolean) => {
      cancelRecheck()
      restoreRef.current?.abort()
      restoreRef.current = null
      // An aborted pass may still await its ask: a new live mustn't say Reconnecting meanwhile.
      setRestoring(false)
      const attempt = attemptRef.current
      attemptRef.current = null
      if (!attempt) return
      attempt.ended = true
      attempt.free = free
      attempt.abort.abort()
      if (free && attempt.seat) void leaveSeat(attempt)
    },
    [cancelRecheck],
  )

  /** Off the stage. Without `free` the API isn't called: the seat has gone elsewhere, or with the connection. */
  const drop = useCallback(
    (reason: StagePresenceErrorCode | StagePresenceError, free = false) => {
      endAttempt(free)
      releaseMedia()
      resetMedia()
      setError(typeof reason === 'string' ? presenceError(reason) : reason)
      setPhase('idle')
    },
    [endAttempt, releaseMedia, resetMedia, setPhase],
  )

  /**
   * The stage's members in the room metadata now: LiveKit's own copy, which a full reconnect's rejoin updates before
   * `Reconnected` (`roomMembers` catches up only when React renders, after the `Reconnected` handlers), else the last
   * `roomMembers`.
   */
  const latestMembers = useCallback(
    (stage: string): RoomMembers => {
      const parsed = parseRoomMetadata(room.metadata)
      return parsed?.stages ? (parsed.stages[stage]?.members ?? []) : membersRef.current
    },
    [room],
  )

  /** The latest metadata still lists this attempt's connection: the seat is known to be on it. */
  const listedHere = useCallback(
    (attempt: Attempt) => Boolean(listedMember(latestMembers(attempt.stageId), attempt.identity)),
    [latestMembers],
  )

  /**
   * Another connection of this attempt's user that the API raised on this stage and LiveKit lets publish: a tab that
   * just went live. The API raises it and lowers this connection before it publishes the metadata, so the
   * metadata may still list this one.
   */
  const publishingElsewhere = useCallback(
    (attempt: Attempt) => {
      const userId = attempt.userId ?? identityUserId(attempt.identity)
      if (userId === null) return false
      return Array.from(room.remoteParticipants.values()).some(
        ({ identity, permissions, metadata }) =>
          identity !== attempt.identity && identityUserId(identity) === userId &&
          Boolean(permissions?.canPublish) && raisedOnStage(metadata, attempt.stageId),
      )
    },
    [room],
  )

  /** Another tab of this attempt's user holds the seat now: the metadata seats it, or LiveKit lets it publish here. */
  const movedElsewhere = useCallback(
    (attempt: Attempt) => seatedElsewhere(latestMembers(attempt.stageId) ?? [], attempt) || publishingElsewhere(attempt),
    [latestMembers, publishingElsewhere],
  )

  /**
   * The seat End gave back needs nothing more: the room closed (the API frees a closed connection's seat), the latest
   * metadata no longer lists this connection, or another tab of this user can publish on this stage (`DELETE` would
   * free its seat).
   */
  const seatSettled = useCallback(
    (attempt: Attempt) => room.state === ConnectionState.Disconnected || !listedHere(attempt) || publishingElsewhere(attempt),
    [room, listedHere, publishingElsewhere],
  )

  /** Off the stage on metadata without this connection: the seat moved to another tab, or was freed. */
  const dropUnseated = useCallback(
    (attempt: Attempt, members: readonly RoomMember[]) => drop(seatedElsewhere(members, attempt) ? 'seat_lost' : 'left_stage'),
    [drop],
  )

  /**
   * The room metadata's word on this attempt's seat. Listing this connection confirms it. Not listing it counts once the
   * metadata has listed it since the API's answer, or after the slack: off the stage, without calling the API. While
   * LiveKit still lets this connection publish both tracks, the latest metadata decides `SEAT_SETTLE_MS` later (one
   * snapshot can be stale; a real move lowers this connection at once), and later updates don't postpone it.
   */
  const checkSeat = useCallback(
    (attempt: Attempt, members: RoomMembers) => {
      if (!members) return
      const member = listedMember(members, attempt.identity)
      if (member) {
        attempt.listed = true
        attempt.userId = member.userId
        attempt.endRefusedAt = null
        cancelRecheck()
        return
      }
      if (!attempt.listed && Date.now() - attempt.answeredAt < SEAT_SETTLE_MS) return
      const tracks = tracksRef.current
      if (!tracks || !publishingBoth(room.localParticipant, tracks)) {
        dropUnseated(attempt, members)
        return
      }
      if (recheckRef.current !== null) return
      recheckRef.current = window.setTimeout(() => {
        recheckRef.current = null
        // A restore checks the latest metadata itself once it finishes.
        if (attemptRef.current !== attempt || restoreRef.current || leavingRef.current) return
        const latest = latestMembers(attempt.stageId)
        if (latest && !listedMember(latest, attempt.identity)) dropUnseated(attempt, latest)
      }, SEAT_SETTLE_MS)
    },
    [room, cancelRecheck, latestMembers, dropUnseated],
  )

  /** The API answered with the seat: the metadata's slack starts again. */
  const seated = useCallback(
    (attempt: Attempt) => {
      attempt.seat = true
      attempt.answeredAt = Date.now()
      const member = listedMember(latestMembers(attempt.stageId), attempt.identity)
      attempt.listed = Boolean(member)
      if (member) attempt.userId = member.userId
    },
    [latestMembers],
  )

  /** A new go live holds the seat now, or the screen is going. */
  const stopFreeing = useCallback(() => {
    freeingRef.current?.abort()
    freeingRef.current = null
    unfreedRef.current = null
    setFreeing(false)
  }, [])

  /**
   * Gives the seat back after End: `DELETE /live?identity=` now, or after `refused` (End's refusal, or a restore's 429).
   * A network failure, 429 or 5xx tries once more after its wait (the Retry-After, at most 60 s, or about 2 s) unless
   * the seat is settled (`seatSettled`). `freeing` meanwhile. If it fails while the seat is still here: `spot_held`, at
   * once when idle, or when a camera check opened meanwhile is cancelled (going live takes the seat anyway).
   */
  const freeSeat = useCallback(
    async (attempt: Attempt, refused: WatchApiFailure | null = null) => {
      // A new go live holds the seat now.
      if (attemptRef.current) return
      freeingRef.current?.abort()
      unfreedRef.current = null
      const run = new AbortController()
      freeingRef.current = run
      setFreeing(true)
      let failure = refused ?? (await leaveSeat(attempt))
      if (failure && retryable(failure) && !run.signal.aborted) {
        await pause(retryDelayMs(1, { retryAfterMs: failure.retryAfterMs }), run.signal)
        failure = run.signal.aborted || seatSettled(attempt) ? null : await leaveSeat(attempt)
      }
      if (freeingRef.current !== run) return
      freeingRef.current = null
      setFreeing(false)
      if (!failure || seatSettled(attempt)) return
      unfreedRef.current = attempt
      if (phaseRef.current === 'idle') setError(presenceError('spot_held'))
    },
    [seatSettled],
  )

  const refreshDevices = useCallback(async () => {
    const tracks = tracksRef.current
    if (!tracks) return
    const [videoInputs, audioInputs, currentCamera, currentMicrophone] = await Promise.all([
      listDevices('videoinput'),
      listDevices('audioinput'),
      tracks.camera?.getDeviceId().catch(() => undefined),
      tracks.mic.getDeviceId().catch(() => undefined),
    ])
    if (tracksRef.current !== tracks) return
    setCameras(videoInputs)
    setMicrophones(audioInputs)
    setCameraId(currentCamera ?? null)
    setMicrophoneId(currentMicrophone ?? null)
  }, [])

  const check = useCallback(async () => {
    if (phaseRef.current === 'joining' || phaseRef.current === 'live') return
    if (tracksRef.current || openingRef.current) return
    setRole('host')
    setError(null)
    setPhase('checking')
    // Off https (an http address on a network) the browser has no camera to give, and refuses before it asks.
    const unavailable = mediaUnavailable()
    if (unavailable) {
      setError(presenceError(unavailable))
      return
    }
    const opening = {}
    openingRef.current = opening

    let opened: LocalTrack[]
    try {
      // A face, not a course camera: 720p from the front camera (the provider's capture default is 1080p).
      opened = await createLocalTracks({
        audio: true,
        video: { resolution: VideoPresets.h720.resolution, facingMode: 'user' },
      })
    } catch (failure) {
      if (openingRef.current !== opening) return
      let code = mediaErrorCode(failure)
      if (code === 'media_missing') code = await missingMedia(failure)
      if (openingRef.current !== opening) return
      openingRef.current = null
      setError(presenceError(code))
      return
    }
    // Cancelled or unmounted while the browser asked: turn them straight off.
    if (openingRef.current !== opening) {
      opened.forEach((track) => track.stop())
      return
    }
    openingRef.current = null
    const video = opened.find((track): track is LocalVideoTrack => track.kind === Track.Kind.Video)
    const audio = opened.find((track): track is LocalAudioTrack => track.kind === Track.Kind.Audio)
    if (!video || !audio) {
      opened.forEach((track) => track.stop())
      setError(presenceError('media_failed'))
      return
    }
    tracksRef.current = { camera: video, mic: audio }
    micOnRef.current = !audio.isMuted
    cameraOnRef.current = !video.isMuted
    setCamera(video)
    setMedia(tracksRef.current)
    setMicOn(micOnRef.current)
    setCameraOn(cameraOnRef.current)
    await refreshDevices()
  }, [refreshDevices, setPhase, setRole])

  /**
   * Going live; `automatic` is the one retry after a 409 `connection_gone`, which doesn't retry again. False when it
   * couldn't start (no check open, no stage, or the room isn't connected as this tab's identity).
   */
  const startLive = useCallback(
    async (automatic: boolean): Promise<boolean> => {
      const { stageId: stage, identity } = optionsRef.current
      const tracks = tracksRef.current
      const participant = room.localParticipant
      // The identity the room is connected as: a new watch token's identity isn't a connection until the room joins.
      const connection = participant.identity
      if (phaseRef.current !== 'checking' || !tracks || !stage || !identity) return false
      if (room.state !== ConnectionState.Connected || connection !== identity) return false
      const attempt: Attempt = {
        stageId: stage,
        role: roleRef.current,
        left: false,
        identity: connection,
        sid: participant.sid,
        seat: false,
        answeredAt: 0,
        listed: false,
        userId: null,
        endRefusedAt: null,
        askLanded: false,
        ended: false,
        free: false,
        abort: new AbortController(),
      }
      attemptRef.current = attempt
      // A seat still being given back after End is this go live's now.
      stopFreeing()
      setReconnectFrom(null)
      setError(null)
      setPhase('joining')

      let answer: unknown
      try {
        answer = await watchApi.goLive(stage, connection)
      } catch (failure) {
        if (attempt.ended) return true
        attemptRef.current = null
        const refused = apiFailure(failure)
        const code = goLiveErrorCode(refused)
        if (code === 'connection_gone') {
          optionsRef.current.onReconnectNeeded()
          if (!automatic) {
            setReconnectFrom(connection)
            setError(presenceError('reconnecting'))
            setPhase('checking')
            return true
          }
        }
        if (code === 'username_required') optionsRef.current.onUsernameNeeded?.()
        setError(refusal(code, refused))
        setPhase('checking')
        return true
      }
      // The server says who this is on the stage: its hosts go live as hosts, its invited guests as guests.
      const served = servedRole(answer)
      if (served) {
        setRole(served)
        attempt.role = served
      }
      seated(attempt)
      if (attempt.ended) {
        if (attempt.free) void leaveSeat(attempt)
        return true
      }

      try {
        await waitForCanPublish(participant, PERMISSION_TIMEOUT_MS, attempt.abort.signal)
      } catch {
        if (attempt.ended) return true
        // LiveKit never raised this connection: give the seat back and keep the check open to try again.
        endAttempt(true)
        setError(presenceError('permission_timeout'))
        setPhase('checking')
        return true
      }
      if (attempt.ended) return true

      try {
        for (const [track, options] of publications(tracks)) {
          await participant.publishTrack(track, options)
          if (attempt.ended) {
            void participant.unpublishTrack(track, true).catch(noop)
            return true
          }
          publishedRef.current.add(track)
        }
      } catch {
        if (attempt.ended) return true
        // Back to the check with the preview running: unpublish without stopping.
        publishedRef.current.forEach((track) => void participant.unpublishTrack(track, false).catch(noop))
        publishedRef.current.clear()
        // The seat is still this tab's unless another tab took it and lowered this one; after a full reconnect (the rejoin
        // can't publish) the latest metadata says whether it is.
        endAttempt(Boolean(participant.permissions?.canPublish) || listedHere(attempt))
        setError(presenceError('publish_failed'))
        setPhase('checking')
        return true
      }
      // Lowered while publishing without this tab hearing of it: the seat is elsewhere, so never say live. A full
      // reconnect meanwhile isn't that: live, and its `Reconnected` asks for the seat again.
      if (!participant.permissions?.canPublish && !rejoining(room, attempt)) {
        drop('seat_lost')
        return true
      }
      // Connected, this is the session that's live (a reconnect meanwhile may have rejoined as a new one).
      if (room.state === ConnectionState.Connected) attempt.sid = participant.sid
      setPhase('live')
      return true
    },
    [room, seated, stopFreeing, endAttempt, listedHere, drop, setPhase, setRole],
  )
  const goLive = useCallback(async () => {
    await startLive(false)
  }, [startLive])

  /**
   * What the room says after a reconnect, from the metadata LiveKit holds at `Reconnected` and its participants
   * (`rejoined`: a new LiveKit session, a full reconnect):
   * - Another tab of this user seated: the seat moved (asking would take it back), off the stage.
   * - A rejoin whose metadata doesn't list this connection: the seat was freed meanwhile, off the stage at once, even
   *   when LiveKit republished on a refreshed token (the join response's metadata is current).
   * - LiveKit still publishes both tracks (a resume keeps all of it): nothing to ask.
   * - Otherwise ask only while the metadata lists this connection (no seat: freed meanwhile, and going back on takes a
   *   tap; no metadata says nothing).
   */
  const afterReconnect = useCallback(
    (attempt: Attempt, tracks: CheckedTracks, rejoined: boolean): 'ask' | 'kept' | 'ended' => {
      const members = latestMembers(attempt.stageId)
      if (members && seatedElsewhere(members, attempt)) {
        drop('seat_lost')
        return 'ended'
      }
      const member = listedMember(members, attempt.identity)
      if (members && !member && rejoined) {
        drop('left_stage')
        return 'ended'
      }
      if (publishingBoth(room.localParticipant, tracks)) return 'kept'
      if (!member) {
        drop(members ? 'left_stage' : 'disconnected')
        return 'ended'
      }
      attempt.listed = true
      attempt.userId = member.userId
      return 'ask'
    },
    [room, latestMembers, drop],
  )

  /**
   * One restore pass after a reconnect while live (each `Reconnected` starts one and aborts the running one, cutting
   * its waits short). Asks for the seat again when `afterReconnect` says so (if another tab of this user is raised on
   * this stage, only when the metadata still says so `SEAT_SETTLE_MS` later), once more a moment later after a network
   * failure (a 429's Retry-After is the throttle's whole minute), waits for the raise, publishes what LiveKit couldn't
   * republish, then lets the latest metadata have its say. Superseded, on a room that isn't connected (its
   * `Reconnected` starts the next pass), with End under way or the attempt ended, it stops without a word. Otherwise a
   * 409 or 502 means the API gave the seat up; any other refusal, a missing raise or a failed publish leaves the seat on
   * a connection that can't publish, so it's freed while the metadata lists this connection (after a 429, by
   * `freeSeat` once the Retry-After is up).
   */
  const restore = useCallback(
    async (attempt: Attempt) => {
      restoreRef.current?.abort()
      const pass = new AbortController()
      restoreRef.current = pass
      setRestoring(true)
      const participant = room.localParticipant
      const tracks = tracksRef.current
      const rejoined = participant.sid !== attempt.sid
      attempt.sid = participant.sid
      const stale = () => pass.signal.aborted || attempt.ended || leavingRef.current || room.state !== ConnectionState.Connected
      try {
        if (!tracks) return
        let next = afterReconnect(attempt, tracks, rejoined)
        if (next === 'ask' && publishingElsewhere(attempt)) {
          await pause(SEAT_SETTLE_MS, pass.signal)
          if (stale()) return
          next = afterReconnect(attempt, tracks, rejoined)
        }
        if (next === 'ended') return
        if (next === 'ask') {
          let failure = await askAgain(attempt)
          if (failure?.status === null && !stale()) {
            await pause(retryDelayMs(1), pass.signal)
            if (stale() || afterReconnect(attempt, tracks, rejoined) !== 'ask') return
            failure = await askAgain(attempt)
          }
          if (!failure && (leavingRef.current || attempt.ended)) {
            // End was tapped while the ask was in flight, and the ask took the seat again: End frees it once more.
            if (!attempt.ended) attempt.askLanded = true
            else if (attempt.free) void freeSeat(attempt)
            return
          }
          if (stale()) return
          if (failure) {
            const gaveUp = failure.status === 409 || failure.status === 502
            const held = !gaveUp && listedHere(attempt)
            // A 429's DELETE would land in the same throttled minute: it goes after the Retry-After instead.
            const throttled = failure.status === 429
            drop(restoreRefusal(failure, roleRef.current), held && !throttled)
            if (held && throttled) void freeSeat(attempt, failure)
            return
          }
          await waitForCanPublish(participant, PERMISSION_TIMEOUT_MS, pass.signal)
          for (const [track, options] of publications(tracks)) {
            if (stale()) return
            if (isPublished(participant, track)) continue
            await participant.publishTrack(track, options)
            if (attempt.ended) {
              void participant.unpublishTrack(track, true).catch(noop)
              return
            }
            publishedRef.current.add(track)
          }
        }
        if (!stale()) checkSeat(attempt, latestMembers(attempt.stageId))
      } catch {
        // The seat is this connection's but it can't publish: give it back while the metadata still lists it here.
        if (!stale()) drop('disconnected', listedHere(attempt))
      } finally {
        if (restoreRef.current === pass) restoreRef.current = null
        // A newer pass may be running by now.
        if (!restoreRef.current) setRestoring(false)
      }
    },
    [room, afterReconnect, publishingElsewhere, freeSeat, listedHere, drop, checkSeat, latestMembers],
  )

  const cancel = useCallback(() => {
    if (phaseRef.current === 'live' || phaseRef.current === 'idle') return
    endAttempt(true)
    releaseMedia()
    resetMedia()
    setReconnectFrom(null)
    // A seat End couldn't free is still this tab's unless it has settled since.
    const unfreed = unfreedRef.current
    setError(unfreed && !seatSettled(unfreed) ? presenceError('spot_held') : null)
    setPhase('idle')
  }, [endAttempt, releaseMedia, resetMedia, seatSettled, setPhase])

  /**
   * The guest's join: the devices the page's check picked, opened on or off as picked, then `startLive`. What happens is
   * told by the settle effect below (live, or a refusal or a cancel).
   */
  const openAndGoLive = useCallback(
    async (choice: StageJoinChoice) => {
      const unavailable = mediaUnavailable()
      if (unavailable) {
        setError(presenceError(unavailable))
        return
      }
      const opening = {}
      openingRef.current = opening
      let opened: LocalTrack[]
      try {
        // Exactly the devices the check showed (the guest picked them there), the camera at a face's 720p.
        opened = await createLocalTracks({
          audio: choice.audioDeviceId ? { deviceId: { exact: choice.audioDeviceId } } : true,
          video: choice.videoDeviceId ? { deviceId: { exact: choice.videoDeviceId }, resolution: VideoPresets.h720.resolution } : false,
        })
      } catch (failure) {
        if (openingRef.current !== opening) return
        openingRef.current = null
        setError(presenceError(mediaErrorCode(failure)))
        return
      }
      if (openingRef.current !== opening) {
        opened.forEach((track) => track.stop())
        return
      }
      const video = opened.find((track): track is LocalVideoTrack => track.kind === Track.Kind.Video) ?? null
      const audio = opened.find((track): track is LocalAudioTrack => track.kind === Track.Kind.Audio)
      if (!audio || (choice.videoDeviceId && !video)) {
        openingRef.current = null
        opened.forEach((track) => track.stop())
        setError(presenceError('media_failed'))
        return
      }
      // Off as picked: a muted camera stops capturing (its light goes off) and publishes as off.
      if (!choice.micOn) await audio.mute().catch(noop)
      if (video && !choice.cameraOn) await video.mute().catch(noop)
      if (openingRef.current !== opening) {
        opened.forEach((track) => track.stop())
        return
      }
      openingRef.current = null
      tracksRef.current = { camera: video, mic: audio }
      micOnRef.current = !audio.isMuted
      cameraOnRef.current = Boolean(video && !video.isMuted)
      setCamera(video)
      setMedia(tracksRef.current)
      setMicOn(micOnRef.current)
      setCameraOn(cameraOnRef.current)
      void refreshDevices()
      if (!(await startLive(false))) setError(presenceError('not_connected'))
    },
    [refreshDevices, startLive],
  )

  const join = useCallback(
    (choice: StageJoinChoice): Promise<void> => {
      if (phaseRef.current === 'joining' || phaseRef.current === 'live') {
        return Promise.reject(new StagePresenceFailure(wordsFor(presenceError('already_on_stage'), 'guest')))
      }
      // A check still open (a host's preview, say) gives way: these are the devices the guest picked.
      if (tracksRef.current || openingRef.current) {
        releaseMedia()
        resetMedia()
      }
      joinRef.current?.reject(new StagePresenceFailure(wordsFor(presenceError('cancelled'), 'guest')))
      const settled = new Promise<void>((resolve, reject) => {
        joinRef.current = { resolve, reject, started: false }
      })
      setRole('guest')
      setReconnectFrom(null)
      setError(null)
      setPhase('checking')
      void openAndGoLive(choice)
      return settled
    },
    [releaseMedia, resetMedia, setRole, setPhase, openAndGoLive],
  )

  // A join settles once live, or once it can't get there: back to idle (a cancel, a drop), or the check with a refusal
  // that isn't the wait for a new connection. The camera and microphone go off before it rejects, so the page's check
  // can open them again.
  useEffect(() => {
    const pending = joinRef.current
    if (!pending) return
    if (!pending.started) {
      // An idle from before this join (its own phase comes with its first render).
      if (state === 'idle') return
      pending.started = true
    }
    if (state === 'live') {
      joinRef.current = null
      pending.resolve()
      return
    }
    const failed = state === 'idle' ? (error ?? presenceError('cancelled')) : state === 'checking' && error && error.code !== 'reconnecting' ? error : null
    if (!failed) return
    joinRef.current = null
    if (state === 'checking') cancel()
    pending.reject(new StagePresenceFailure(wordsFor(failed, 'guest')))
  }, [state, error, cancel])

  /**
   * End. Refused while LiveKit publishes both tracks: still live, the latest metadata has its say, and it rethrows for
   * End's dialog; on a closed room: off the stage (the API frees a closed connection's seat). Otherwise End wins: off
   * the stage, freeing the seat again after a refusal, or after an ask that took it again meanwhile.
   */
  const leave = useCallback(async () => {
    const attempt = attemptRef.current
    if (phaseRef.current !== 'live' || !attempt) {
      cancel()
      return
    }
    leavingRef.current = true
    const refused = await watchApi.leave(attempt.stageId, attempt.identity).then(() => null, (error: unknown) => ({ error }))
    leavingRef.current = false
    // Something else ended it meanwhile (a new connection while waiting for this answer).
    if (attemptRef.current !== attempt || phaseRef.current !== 'live') return
    if (refused && room.state === ConnectionState.Disconnected) {
      drop('disconnected')
      return
    }
    const tracks = tracksRef.current
    if (refused && tracks && publishingBoth(room.localParticipant, tracks)) {
      checkSeat(attempt, latestMembers(attempt.stageId))
      // After that check: the snapshot it read may predate End's DELETE.
      attempt.endRefusedAt = Date.now()
      throw refused.error
    }
    endAttempt(false)
    // A restore's ask still in flight takes the seat again when it lands: it frees it then. Either way it is this
    // person leaving (a guest's invitation used up).
    attempt.free = true
    attempt.left = true
    releaseMedia()
    resetMedia()
    setError(null)
    setPhase('idle')
    if (refused) void freeSeat(attempt, apiFailure(refused.error))
    else if (attempt.askLanded) void freeSeat(attempt)
  }, [room, cancel, drop, checkSeat, latestMembers, endAttempt, freeSeat, releaseMedia, resetMedia, setPhase])

  /** Set at once, reverted on failure. Muting a camera stops its capture (the light goes off); unmuting, or turning
   * on a track that stopped, opens it again. */
  const toggle = useCallback(
    async (kind: MediaKind) => {
      const track = tracksRef.current?.[kind]
      if (!track) return
      const on = !(kind === 'camera' ? cameraOnRef.current : micOnRef.current)
      setOn(kind, on)
      try {
        await (on ? track.unmute() : track.mute())
      } catch {
        if (tracksRef.current?.[kind] !== track) return
        setOn(kind, !on)
        setError(presenceError('media_failed'))
        return
      }
      if (on && tracksRef.current?.[kind] === track) setError((current) => (current?.code === STOPPED[kind] ? null : current))
    },
    [setOn],
  )
  const toggleMic = useCallback(() => toggle('mic'), [toggle])
  const toggleCamera = useCallback(() => toggle('camera'), [toggle])

  const selectDevice = useCallback(
    async (kind: MediaKind, deviceId: string) => {
      const track = tracksRef.current?.[kind]
      if (!track) return
      const setId = kind === 'camera' ? setCameraId : setMicrophoneId
      const previous = await track.getDeviceId().catch(() => undefined)
      // Closed meanwhile: switching a stopped track would open the device again, with nothing left to close it.
      if (tracksRef.current?.[kind] !== track) return
      setId(deviceId)
      let switched: boolean
      try {
        // Published or not, the same track switches capture; LiveKit swaps it on the sender and in the preview.
        // `exact`, or the browser may keep the current device and call it done.
        switched = await track.setDeviceId({ exact: deviceId })
      } catch {
        switched = false
      }
      if (switched || tracksRef.current?.[kind] !== track) return
      setId(previous ?? null)
      setError(presenceError('switch_failed'))
      // Whatever the track ended up on is what the picker shows.
      void refreshDevices()
    },
    [refreshDevices],
  )
  const selectCamera = useCallback((deviceId: string) => selectDevice('camera', deviceId), [selectDevice])
  const selectMicrophone = useCallback((deviceId: string) => selectDevice('mic', deviceId), [selectDevice])

  // Plugging in a camera or headset updates the pickers while the tracks are open.
  useEffect(() => {
    if (!media) return
    const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices
    if (!devices?.addEventListener) return
    const onChange = () => void refreshDevices()
    devices.addEventListener('devicechange', onChange)
    return () => devices.removeEventListener('devicechange', onChange)
  }, [media, refreshDevices])

  // A camera or microphone that stops on its own (unplugged, taken by another app or the system) shows as off, and is
  // muted so the audience sees it off rather than a frozen frame; turning it on opens the capture again. A restart
  // LiveKit makes on its own (not muted) shows it on again.
  useEffect(() => {
    const tracks = tracksRef.current
    if (!media || tracks !== media) return
    const listeners = (Object.keys(tracks) as MediaKind[]).flatMap((kind) => {
      const track = tracks[kind]
      if (!track) return []
      const onEnded = () => {
        if (tracksRef.current !== tracks) return
        setOn(kind, false)
        void track.mute().catch(noop)
        setError(presenceError(STOPPED[kind]))
      }
      const onRestarted = () => {
        if (tracksRef.current !== tracks || track.isMuted) return
        setOn(kind, true)
        setError((current) => (current?.code === STOPPED[kind] ? null : current))
      }
      track.on(TrackEvent.Ended, onEnded)
      track.on(TrackEvent.Restarted, onRestarted)
      return [
        () => {
          track.off(TrackEvent.Ended, onEnded)
          track.off(TrackEvent.Restarted, onRestarted)
        },
      ]
    })
    return () => listeners.forEach((stop) => stop())
  }, [media, setOn])

  // While going live or live. A permission drop during a full reconnect (`rejoining`) is the token's: the reconnect
  // decides. Otherwise the API lowered this connection (another tab went live, the host left elsewhere): off the stage
  // without calling the API (while joining, once the API gave it the seat); just after a refused End, the live ended.
  // During End, End decides. Each `Reconnected` while live starts a restore pass. A closed connection ends it (after
  // End's answer, during End).
  useEffect(() => {
    if (state !== 'joining' && state !== 'live') return
    const participant = room.localParticipant
    const onPermissions = (previous?: ParticipantPermission) => {
      if (participant.permissions?.canPublish || !previous?.canPublish) return
      const attempt = attemptRef.current
      if (!attempt || leavingRef.current || rejoining(room, attempt)) return
      const phase = phaseRef.current
      if (phase !== 'live' && !(phase === 'joining' && attempt.seat)) return
      if (endJustRefused(attempt)) drop('live_ended')
      // A guest lowered with no other tab of theirs seated was taken off the stage (by a host, or a leave elsewhere).
      else if (roleRef.current === 'guest' && !movedElsewhere(attempt)) drop('left_stage')
      else drop('seat_lost')
    }
    const onReconnected = () => {
      const attempt = attemptRef.current
      if (!attempt) return
      if (!leavingRef.current && phaseRef.current === 'live') void restore(attempt)
      // No restore (End under way, or still joining): the session is current all the same, so a later resume isn't a rejoin.
      else attempt.sid = room.localParticipant.sid
    }
    const onDisconnected = () => {
      if (leavingRef.current || (phaseRef.current !== 'joining' && phaseRef.current !== 'live')) return
      drop('disconnected')
    }
    participant.on(ParticipantEvent.ParticipantPermissionsChanged, onPermissions)
    room.on(RoomEvent.Reconnected, onReconnected)
    room.on(RoomEvent.Disconnected, onDisconnected)
    return () => {
      participant.off(ParticipantEvent.ParticipantPermissionsChanged, onPermissions)
      room.off(RoomEvent.Reconnected, onReconnected)
      room.off(RoomEvent.Disconnected, onDisconnected)
    }
  }, [state, room, drop, restore, movedElsewhere])

  // Each room metadata update while going live or live (see `checkSeat`); a restore checks the latest once it finishes.
  useEffect(() => {
    membersRef.current = roomMembers
    const attempt = attemptRef.current
    const phase = phaseRef.current
    if (!attempt?.seat || restoreRef.current || leavingRef.current) return
    if (phase !== 'joining' && phase !== 'live') return
    checkSeat(attempt, roomMembers)
  }, [roomMembers, checkSeat])

  // A new identity is a new connection: the one that went live is gone (the API frees its seat). The check's tracks
  // stay open for the new connection.
  const identityRef = useRef(identity)
  useEffect(() => {
    if (identityRef.current === identity) return
    identityRef.current = identity
    if (phaseRef.current === 'joining' || phaseRef.current === 'live') drop('disconnected')
  }, [identity, drop])

  const connectedAs = Boolean(
    identity && connectionState === ConnectionState.Connected && room.localParticipant.identity === identity,
  )
  // The tracks are open (a guest may have the microphone alone), there is a stage, and the room is connected as this tab.
  const ready = Boolean(media && stageId && connectedAs)

  // After a 409 `connection_gone`: once the room has joined as the new identity, go live again by itself (once). If it
  // doesn't join in time, stop saying Reconnecting.
  useEffect(() => {
    if (!reconnectFrom || !ready || identity === reconnectFrom) return
    setReconnectFrom(null)
    void startLive(true)
  }, [reconnectFrom, ready, identity, startLive])
  useEffect(() => {
    if (!reconnectFrom) return
    const timer = window.setTimeout(() => {
      setReconnectFrom(null)
      setError((current) => (current?.code === 'reconnecting' ? presenceError('connection_gone') : current))
    }, RECONNECT_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [reconnectFrom])

  // Leaving the screen while going live gives the seat back, and while live too while the latest metadata lists this
  // connection (never another tab's; the API frees a closed connection's seat anyway). The state goes back to idle too:
  // Fast Refresh, StrictMode and a new room run this cleanup on a hook that stays mounted.
  useEffect(
    () => () => {
      const phase = phaseRef.current
      const attempt = attemptRef.current
      const pending = joinRef.current
      joinRef.current = null
      pending?.reject(new StagePresenceFailure(wordsFor(presenceError('cancelled'), 'guest')))
      endAttempt(phase === 'joining' || (phase === 'live' && attempt !== null && listedHere(attempt)))
      releaseMedia()
      leavingRef.current = false
      stopFreeing()
      resetMedia()
      setReconnectFrom(null)
      setError(null)
      setPhase('idle')
    },
    [endAttempt, listedHere, releaseMedia, stopFreeing, resetMedia, setPhase],
  )

  const canGoLive = ready && reconnectFrom === null
  const reconnecting = state === 'live' && (connectionState === ConnectionState.Reconnecting || restoring)
  const shownError = useMemo(() => error && wordsFor(error, role), [error, role])
  // One object while nothing in it changes, so a memoised dock (and the stage around it) can skip the page's renders.
  return useMemo(
    () => ({
      state,
      role,
      error: shownError,
      camera,
      micOn,
      cameraOn,
      cameras,
      microphones,
      cameraId,
      microphoneId,
      canGoLive,
      reconnecting,
      freeing,
      check,
      goLive,
      join,
      cancel,
      leave,
      toggleMic,
      toggleCamera,
      selectCamera,
      selectMicrophone,
    }),
    [
      state, role, shownError, camera, micOn, cameraOn, cameras, microphones, cameraId, microphoneId, canGoLive, reconnecting,
      freeing, check, goLive, join, cancel, leave, toggleMic, toggleCamera, selectCamera, selectMicrophone,
    ],
  )
}

/** Resolves once LiveKit lets this connection publish; rejects after `timeoutMs` or when `signal` aborts. */
function waitForCanPublish(participant: LocalParticipant, timeoutMs: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (participant.permissions?.canPublish) {
      resolve()
      return
    }
    if (signal.aborted) {
      reject(new Error('aborted'))
      return
    }
    const finish = (failure?: Error) => {
      window.clearTimeout(timer)
      participant.off(ParticipantEvent.ParticipantPermissionsChanged, onChange)
      signal.removeEventListener('abort', onAbort)
      if (failure) reject(failure)
      else resolve()
    }
    const onChange = () => {
      if (participant.permissions?.canPublish) finish()
    }
    const onAbort = () => finish(new Error('aborted'))
    const timer = window.setTimeout(() => finish(new Error('permission_timeout')), timeoutMs)
    participant.on(ParticipantEvent.ParticipantPermissionsChanged, onChange)
    signal.addEventListener('abort', onAbort)
  })
}

/** Waits `ms`, or until `signal` aborts. */
function pause(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const done = () => {
      window.clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const timer = window.setTimeout(done, ms)
    signal.addEventListener('abort', done)
  })
}

/**
 * The browser's cameras or microphones as the pickers list them (`deviceChoices`: each once, never a blank name).
 * Permission was granted by the check, so this never asks again.
 */
async function listDevices(kind: 'videoinput' | 'audioinput'): Promise<StageDevice[]> {
  try {
    return deviceChoices(await Room.getLocalDevices(kind, false), kind)
  } catch {
    return []
  }
}

/** A getUserMedia refusal, by the DOMException's name. */
function mediaErrorCode(failure: unknown): StagePresenceErrorCode {
  const name = (failure as { name?: unknown } | null)?.name
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'media_denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'media_missing'
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'media_busy'
    default:
      return 'media_failed'
  }
}

/**
 * Why the browser has no camera to give before asking: off a secure origin (an http address other than localhost) it
 * hides `mediaDevices`; a few browsers have no getUserMedia at all.
 */
function mediaUnavailable(): 'media_insecure' | 'media_unsupported' | null {
  if (typeof window !== 'undefined' && window.isSecureContext === false) return 'media_insecure'
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return 'media_unsupported'
  return null
}

/**
 * Which of the camera and microphone is missing after a NotFoundError or OverconstrainedError: getUserMedia asks for
 * both and doesn't say. The browser lists each kind it has before permission is granted, without names. An
 * OverconstrainedError with both kinds listed is missing nothing (a device couldn't meet the ask): the generic line.
 */
async function missingMedia(failure: unknown): Promise<StagePresenceErrorCode> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const has = (kind: MediaDeviceKind) => devices.some((device) => device.kind === kind)
    if (has('audioinput') && !has('videoinput')) return 'camera_missing'
    if (has('videoinput') && !has('audioinput')) return 'mic_missing'
    if (has('videoinput') && has('audioinput') && (failure as { name?: unknown } | null)?.name === 'OverconstrainedError') {
      return 'media_failed'
    }
  } catch {
    // Unknown: say both.
  }
  return 'media_missing'
}

const GO_LIVE_ERRORS = [
  'connection_gone',
  'already_on_stage',
  'stage_full',
  'username_required',
  'forbidden',
  'livekit_unavailable',
  'rate_limited',
] as const satisfies readonly StagePresenceErrorCode[]

/** A refused go live (Task 7's errors), by type and status. */
function goLiveErrorCode(failure: WatchApiFailure): StagePresenceErrorCode {
  if (failure.status === null) return 'network'
  const known = GO_LIVE_ERRORS.find((code) => code === failure.type)
  if (known) return known
  if (failure.status === 429) return 'rate_limited'
  if (failure.status === 403) return 'forbidden'
  if (failure.status === 502) return 'livekit_unavailable'
  return 'go_live_failed'
}
